// src/services/piiService.js

const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;


// -----------------------------
// PII FILE ANONYMIZATION
// -----------------------------
export const anonymizeFilePII = async (file, options = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/anonymize-file/`);

    const formData = new FormData();
    formData.append('file', file);

    const safeOptions =
      options && typeof options === 'object' ? options : {};

    // 🔹 Pull out profileId, defaultTemplateId and anonymizationGroupId
    const {
      profileId,
      defaultTemplateId, // <-- NEW
      anonymizationGroupId, // <-- NEW
      ...restOptions
    } = safeOptions;

    // 🔹 Send the group id as its own field (regardless of mode)
    if (anonymizationGroupId) {
      formData.append(
        'anonymization_group_id',
        String(anonymizationGroupId)
      );
    }

    if (defaultTemplateId) {
      // Template mode
      formData.append(
        'default_anonymization_profile_template_id',
        String(defaultTemplateId)
      );
      // keep existing behavior: no "options" field in this branch
    } else if (profileId) {
      // Profile mode: backend will load AnonymizationProfile by id
      formData.append('anonymization_profile_id', String(profileId));
      // No "options" field when using a profile
    } else {
      // Custom options mode: send options JSON
      formData.append('options', JSON.stringify(restOptions));
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        // DON'T set Content-Type manually for FormData
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { detail: 'Failed to anonymize file.' };
      }
      return {
        success: false,
        data: null,
        statusCode: response.status,
        error: errorBody.detail || errorBody,
      };
    }

    // Get anonymized file as Blob
    const blob = await response.blob();

    // Try to extract filename from Content-Disposition
    const contentDisposition =
      response.headers.get('Content-Disposition') || '';
    let filename = 'anonymized_file';
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    }

    return {
      success: true,
      data: { blob, filename },
      statusCode: response.status,
      error: null,
    };
  } catch (error) {
    console.error('Error anonymizing file PII', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};


// -----------------------------
// PII MANUAL RECTANGLE REDACTION
// -----------------------------
export const anonymizeFilePIIWithRectangles = async (
  file,
  rectangles = [],
  options = {}
) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/anonymize-file-rectangles/`);

    const formData = new FormData();
    formData.append('file', file);

    // rectangles = [{ pageIndex, x, y, w, h, unit:'fraction', origin:'top-left', ... }]
    formData.append('rectangles', JSON.stringify(Array.isArray(rectangles) ? rectangles : []));

    // Optional: allow passing profile/group the same way as anonymizeFilePII
    const safeOptions = options && typeof options === 'object' ? options : {};
    const { profileId, defaultTemplateId, anonymizationGroupId, ...restOptions } = safeOptions; // <-- NEW

    if (anonymizationGroupId) {
      formData.append('anonymization_group_id', String(anonymizationGroupId));
    }

    if (defaultTemplateId) {
      formData.append(
        'default_anonymization_profile_template_id',
        String(defaultTemplateId)
      );
      // keep existing behavior: no "options" field in this branch
    } else if (profileId) {
      formData.append('anonymization_profile_id', String(profileId));
    } else if (Object.keys(restOptions).length) {
      formData.append('options', JSON.stringify(restOptions));
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { detail: 'Failed to anonymize file with rectangles.' };
      }
      return {
        success: false,
        data: null,
        statusCode: response.status,
        error: errorBody.detail || errorBody,
      };
    }

    const blob = await response.blob();

    const contentDisposition = response.headers.get('Content-Disposition') || '';
    let filename = 'anonymized_file_rectangles';
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: { blob, filename },
      statusCode: response.status,
      error: null,
    };
  } catch (error) {
    console.error('Error anonymizing file PII with rectangles', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

// -----------------------------
// DOCX PSEUDO-ANONYMIZATION
// -----------------------------
export const pseudoAnonymizeDocxPII = async (file, options = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/docx/pseudoanonymize/`);

    const formData = new FormData();
    formData.append('file', file);

    const safeOptions = options && typeof options === 'object' ? options : {};

    const {
      profileId,
      defaultTemplateId,
      anonymizationGroupId,
      ...restOptions
    } = safeOptions;

    if (anonymizationGroupId) {
      formData.append(
        'anonymization_group_id',
        String(anonymizationGroupId)
      );
    }

    if (defaultTemplateId) {
      // Template mode
      formData.append(
        'default_anonymization_profile_template_id',
        String(defaultTemplateId)
      );
      // keep existing behavior: no "options" field in this branch
    } else if (profileId) {
      // Profile mode
      formData.append('anonymization_profile_id', String(profileId));
      // no "options" field when using profile
    } else if (Object.keys(restOptions).length) {
      // Custom options mode
      formData.append('options', JSON.stringify(restOptions));
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorBody = null;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { detail: 'Failed to pseudo-anonymize DOCX.' };
      }
      return {
        success: false,
        data: null,
        statusCode: response.status,
        error: errorBody.detail || errorBody,
      };
    }

    const blob = await response.blob();

    const contentDisposition = response.headers.get('Content-Disposition') || '';
    let filename = 'pseudonymized_file.docx';
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = match[1];
    }

    const vaultId = response.headers.get('X-Pseudonym-Vault-Id');

    return {
      success: true,
      data: { blob, filename, vaultId },
      statusCode: response.status,
      error: null,
    };
  } catch (error) {
    console.error('Error pseudo-anonymizing DOCX PII', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

const decodeBase64Utf8Json = (b64) => {
  if (!b64) return null;
  try {
    // base64 -> bytes -> utf8 string -> json
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const jsonText = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonText);
  } catch {
    return null;
  }
};

export const dePseudoAnonymizeDocxPII = async (file) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/docx/depseudoanonymize/`);

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); }
      catch { errorBody = { detail: 'Failed to de-pseudonymize DOCX.' }; }
      return { success: false, data: null, statusCode: response.status, error: errorBody.detail || errorBody };
    }

    // ✅ read integrity stats headers (must be exposed by CORS)
    const statsB64 = response.headers.get('X-Depseudo-Stats');
    const depseudoStats = decodeBase64Utf8Json(statsB64);

    const replacedCount = Number(response.headers.get('X-Depseudo-Replaced-Count') || 0);
    const mappingTotal = Number(response.headers.get('X-Depseudo-Mapping-Total') || 0);

    const blob = await response.blob();

    const cd = response.headers.get('Content-Disposition') || '';
    let filename = 'depseudonymized.docx';
    const match = cd.match(/filename="?([^"]+)"?/);
    if (match && match[1]) filename = match[1];

    return {
      success: true,
      data: {
        blob,
        filename,
        stats: depseudoStats,
        replacedCount,
        mappingTotal,
      },
      statusCode: response.status,
      error: null,
    };
  } catch (e) {
    return { success: false, data: null, statusCode: 500, error: e.message };
  }
};

// -----------------------------
// PII FILE VALIDATION (GDPR CHECK)
// -----------------------------
export const validateFilePII = async (file, options = {}) => {
  try {
    const url = new URL(`${API_BASE_URL}/anonymization/pii/validate-file/`);

    const formData = new FormData();
    formData.append('file', file);

    const safeOptions = options && typeof options === 'object' ? options : {};

    const {
      profileId,
      defaultTemplateId,
      anonymizationGroupId,
      ...restOptions
    } = safeOptions;

    if (anonymizationGroupId) {
      formData.append('anonymization_group_id', String(anonymizationGroupId));
    }

    if (defaultTemplateId) {
      // Template mode
      formData.append(
        'default_anonymization_profile_template_id',
        String(defaultTemplateId)
      );
      // keep behavior consistent: no "options" field in template mode
    } else if (profileId) {
      // Profile mode
      formData.append('anonymization_profile_id', String(profileId));
      // no "options" field when using profile
    } else if (Object.keys(restOptions).length) {
      // Custom options mode
      formData.append('options', JSON.stringify(restOptions));
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    // Expect JSON either way
    let body = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }

    if (!response.ok) {
      const errorBody = body || { detail: 'Failed to validate file.' };
      return {
        success: false,
        data: null,
        statusCode: response.status,
        error: errorBody.detail || errorBody,
      };
    }

    // Expected response shape:
    // {
    //   filename,
    //   content_type,
    //   pages,
    //   gdpr_values_count,
    //   breakdown: { ... },
    //   should_not_share
    // }
    return {
      success: true,
      data: body,
      statusCode: response.status,
      error: null,
    };
  } catch (error) {
    console.error('Error validating file PII', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};